import { CheckCircle2, Download, Globe, Laptop, Loader2, RefreshCw, RotateCcw, ShieldCheck, Sparkles } from 'lucide-react';
import { Button, Card, CardBody, CardDivider, CardHeader, Notice, ProgressBar } from '../../components/ui';
import { Rows, SettingRow, Stat, StatusPill } from '../../components/settings/SettingsPrimitives';
import { useDesktopUpdates } from '../../hooks/useDesktopUpdates';
import { bytes, relativeTime } from '../../lib/format';
import { SettingsTitle } from './SettingsLayout';

export function UpdatesSettings() {
  const { supported, state, check, download, install, appVersion } = useDesktopUpdates();

  if (!supported) {
    return (
      <div>
        <SettingsTitle title="App & updates" description="Version information and updates for the MailPilot desktop app." />
        <Card>
          <CardHeader icon={<Globe className="h-5 w-5" />} title="You're using MailPilot in the browser" description="The web app is always up to date — reloading the page loads the newest version." actions={<StatusPill tone="success">Up to date</StatusPill>} />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <Rows>
              <SettingRow icon={<Laptop className="h-4 w-4" aria-hidden />} label="Desktop app" description="In-app updates, background ringing and the on-screen island are part of the desktop app for macOS and Windows." />
            </Rows>
          </CardBody>
        </Card>
      </div>
    );
  }

  const s = state;
  const latest = s?.latest;
  const busy = s?.status === 'checking' || s?.status === 'downloading' || s?.status === 'installing';
  const hasUpdate = s?.status === 'available' || s?.status === 'downloading' || s?.status === 'ready' || s?.status === 'installing';
  const statusPill =
    s?.status === 'up-to-date' ? (
      <StatusPill tone="success">Up to date</StatusPill>
    ) : hasUpdate ? (
      <StatusPill tone="primary" pulse={s?.status === 'downloading' || s?.status === 'installing'}>
        Update available
      </StatusPill>
    ) : s?.status === 'error' ? (
      <StatusPill tone="danger">Check failed</StatusPill>
    ) : s?.status === 'checking' ? (
      <StatusPill tone="info" pulse>
        Checking
      </StatusPill>
    ) : undefined;

  return (
    <div>
      <SettingsTitle title="App & updates" description="MailPilot checks for new versions every few hours. Updates are verified before they're installed, and the app restarts by itself." />
      <div className="space-y-6">
        <Card>
          <CardHeader icon={<Laptop className="h-5 w-5" />} title="MailPilot for desktop" description="Your mail, settings and sign-in are kept across updates — only the app itself is replaced." actions={statusPill} />
          <CardDivider className="mt-5" />
          <CardBody className="space-y-5 !py-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="grid gap-x-10 gap-y-4 sm:grid-cols-2">
                <Stat label="Installed version" value={s?.currentVersion ?? appVersion ?? '—'} />
                <Stat label="Last checked" value={s?.checkedAt ? relativeTime(s.checkedAt) : 'Not yet'} hint="Checks again every few hours" />
              </div>
              <Button size="sm" icon={s?.status === 'checking' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <RefreshCw className="h-4 w-4" aria-hidden />} disabled={busy} onClick={() => void check()}>
                Check for updates
              </Button>
            </div>

            {s?.status === 'up-to-date' && (
              <p className="flex items-center gap-2 text-[0.8125rem] font-medium text-success-ink">
                <CheckCircle2 className="h-4 w-4" aria-hidden /> You're on the latest version.
              </p>
            )}
            {s?.status === 'error' && <Notice tone="danger">{s.error}</Notice>}

            {hasUpdate && latest && (
              <div className="rounded-2xl bg-primary-soft/60 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-surface text-primary-ink shadow-sm">
                      <Sparkles className="h-5 w-5" aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">MailPilot {latest.version} is available</p>
                      <p className="mt-0.5 text-xs text-muted">
                        Published {relativeTime(latest.publishedAt)}
                        {latest.size ? ` · ${bytes(latest.size)}` : ''}
                      </p>
                    </div>
                  </div>
                  {s.status === 'available' && (
                    <Button variant="primary" icon={<Download className="h-4 w-4" aria-hidden />} onClick={() => void download()} className="hover-glow">
                      Download update
                    </Button>
                  )}
                  {(s.status === 'ready' || s.status === 'installing') && (
                    <Button variant="primary" icon={<RotateCcw className="h-4 w-4" aria-hidden />} loading={s.status === 'installing'} disabled={!s.canInstall} onClick={() => void install()} className="hover-glow">
                      Restart & update
                    </Button>
                  )}
                </div>
                {latest.notes && <p className="mt-4 whitespace-pre-line text-[0.8125rem] leading-relaxed text-ink">{latest.notes}</p>}
                {s.status === 'downloading' && (
                  <div className="mt-4 space-y-1.5">
                    <ProgressBar value={Math.round((s.progress ?? 0) * 100)} max={100} label="Downloading update" />
                    <p className="text-xs text-muted">Downloading… {Math.round((s.progress ?? 0) * 100)}%</p>
                  </div>
                )}
                {s.status === 'ready' && !s.canInstall && (
                  <p className="mt-3 text-xs text-warn-ink">This copy is running from source code, so it can't replace itself. Install MailPilot from the .dmg to use one-click updates.</p>
                )}
              </div>
            )}
          </CardBody>
          <CardDivider />
          <p className="flex items-start gap-2 px-6 py-4 text-xs leading-relaxed text-muted">
            <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            Updates come from the MailPilot server this app is connected to and are verified before they're installed.
          </p>
        </Card>
      </div>
    </div>
  );
}
